import { useEffect, useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  Type as TypeIcon,
  Link2,
  Mail,
  Wifi,
  Contact,
  Phone,
  MessageSquare,
  Download,
  Image as ImageIcon,
  Loader2,
  Trash2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const QR_TYPES = [
  { id: "text", label: "Text", icon: TypeIcon },
  { id: "url", label: "URL", icon: Link2 },
  { id: "email", label: "E-Mail", icon: Mail },
  { id: "wifi", label: "WLAN", icon: Wifi },
  { id: "vcard", label: "Kontakt", icon: Contact },
  { id: "phone", label: "Telefon", icon: Phone },
  { id: "sms", label: "SMS", icon: MessageSquare },
];

const COLOR_PRESETS = [
  "#000000", "#0A52FF", "#16A34A", "#DC2626", "#9333EA", "#EA580C", "#0891B2", "#52525B",
];

function escapeText(s) { return s ?? ""; }

function buildPayload(type, data) {
  if (type === "text") return data.text || "";
  if (type === "url") {
    let u = (data.url || "").trim();
    if (u && !/^[a-z][a-z0-9+.-]*:\/\//i.test(u)) u = "https://" + u;
    return u;
  }
  if (type === "email") {
    const params = [];
    if (data.subject) params.push(`subject=${encodeURIComponent(data.subject)}`);
    if (data.body) params.push(`body=${encodeURIComponent(data.body)}`);
    return `mailto:${data.email || ""}${params.length ? "?" + params.join("&") : ""}`;
  }
  if (type === "phone") return `tel:${data.phone || ""}`;
  if (type === "sms") return `SMSTO:${data.phone || ""}:${data.message || ""}`;
  if (type === "wifi") {
    const esc = (v) => (v || "").replace(/([\\;,:"])/g, "\\$1");
    const enc = (data.encryption || "WPA").toUpperCase();
    if (enc === "NONE") return `WIFI:T:nopass;S:${esc(data.ssid)};H:${data.hidden ? "true" : "false"};;`;
    return `WIFI:T:${enc};S:${esc(data.ssid)};P:${esc(data.password)};H:${data.hidden ? "true" : "false"};;`;
  }
  if (type === "vcard") {
    const lines = ["BEGIN:VCARD", "VERSION:3.0",
      `N:${data.last_name || ""};${data.first_name || ""};;;`,
      `FN:${[data.first_name, data.last_name].filter(Boolean).join(" ")}`];
    if (data.organization) lines.push(`ORG:${data.organization}`);
    if (data.title) lines.push(`TITLE:${data.title}`);
    if (data.phone) lines.push(`TEL;TYPE=CELL:${data.phone}`);
    if (data.email) lines.push(`EMAIL:${data.email}`);
    if (data.url) lines.push(`URL:${data.url}`);
    if (data.address) lines.push(`ADR:;;${data.address};;;;`);
    lines.push("END:VCARD");
    return lines.join("\n");
  }
  return "";
}

const DEFAULT_DATA = {
  text: "",
  url: "https://makrolog.de",
  email: "",
  subject: "",
  body: "",
  phone: "",
  message: "",
  ssid: "",
  password: "",
  encryption: "WPA",
  hidden: false,
  first_name: "",
  last_name: "",
  organization: "Makrolog AG",
  title: "",
  address: "",
};

export default function QRGenerator() {
  const [type, setType] = useState("url");
  const [data, setData] = useState({ ...DEFAULT_DATA });
  const [fg, setFg] = useState("#0A52FF");
  const [bg, setBg] = useState("#FFFFFF");
  const [errorLevel, setErrorLevel] = useState("H");
  const [scale, setScale] = useState(10);
  const [border, setBorder] = useState(2);
  const [centerMode, setCenterMode] = useState("none"); // none | text | image
  const [centerText, setCenterText] = useState("MA");
  const [logoFile, setLogoFile] = useState(null); // {id, url, content_type}
  const [logoLocalUrl, setLogoLocalUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const fileInputRef = useRef(null);
  const previewRef = useRef(null);

  const payload = useMemo(() => buildPayload(type, data), [type, data]);
  const hasPayload = (payload || "").trim().length > 0;

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const onUpload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Datei größer als 2 MB.");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/files/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoFile(res.data);
      setLogoLocalUrl(URL.createObjectURL(file));
      toast.success("Logo hochgeladen.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    if (logoLocalUrl) URL.revokeObjectURL(logoLocalUrl);
    setLogoLocalUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const generateAndDownload = async (format) => {
    if (!hasPayload) {
      toast.error("Bitte Inhalt eingeben.");
      return;
    }
    setGenerating(true);
    try {
      const opts = {
        fg_color: fg,
        bg_color: bg,
        scale,
        border,
        error: errorLevel,
        center_text: centerMode === "text" ? centerText || null : null,
        logo_id: centerMode === "image" ? (logoFile?.id || null) : null,
      };
      const res = await api.post("/qr/generate", { type, data, options: opts });
      const { png_base64, svg, id } = res.data;
      if (format === "png") {
        downloadDataUrl(`data:image/png;base64,${png_base64}`, `qr-${id}.png`);
      } else {
        const blob = new Blob([svg], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        downloadDataUrl(url, `qr-${id}.svg`);
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      }
      toast.success(`${format.toUpperCase()} heruntergeladen.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generierung fehlgeschlagen");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      {/* Configuration */}
      <section className="lg:col-span-8 space-y-8" data-testid="qr-config-panel">
        <div>
          <div className="overline text-[11px] font-bold uppercase tracking-[0.22em] text-zinc-500">01 · Typ wählen</div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="qr-type-grid">
            {QR_TYPES.map((t) => (
              <button
                key={t.id}
                onClick={() => setType(t.id)}
                data-testid={`qr-type-${t.id}`}
                className={`group flex flex-col items-center gap-1.5 px-3 py-3 rounded-md border text-xs font-medium transition-all ${
                  type === t.id
                    ? "border-zinc-900 bg-zinc-900 text-white shadow-[0_0_0_3px_rgba(9,9,11,0.06)]"
                    : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400"
                }`}
              >
                <t.icon className="w-4 h-4" />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="overline text-[11px] font-bold uppercase tracking-[0.22em] text-zinc-500">02 · Inhalt</div>
          <div className="mt-3 border border-zinc-200 bg-white rounded-md p-5">
            {type === "text" && (
              <FieldGroup>
                <Field label="Text">
                  <Textarea data-testid="field-text" value={data.text} onChange={(e) => update("text", e.target.value)} placeholder="Beliebiger Text…" rows={4} />
                </Field>
              </FieldGroup>
            )}
            {type === "url" && (
              <FieldGroup>
                <Field label="URL">
                  <Input data-testid="field-url" value={data.url} onChange={(e) => update("url", e.target.value)} placeholder="https://makrolog.de" />
                </Field>
              </FieldGroup>
            )}
            {type === "email" && (
              <FieldGroup>
                <Field label="E-Mail-Adresse">
                  <Input data-testid="field-email" type="email" value={data.email} onChange={(e) => update("email", e.target.value)} placeholder="empfaenger@beispiel.de" />
                </Field>
                <Field label="Betreff (optional)">
                  <Input data-testid="field-subject" value={data.subject} onChange={(e) => update("subject", e.target.value)} />
                </Field>
                <Field label="Nachricht (optional)" full>
                  <Textarea data-testid="field-body" value={data.body} onChange={(e) => update("body", e.target.value)} rows={3} />
                </Field>
              </FieldGroup>
            )}
            {type === "phone" && (
              <FieldGroup>
                <Field label="Telefonnummer">
                  <Input data-testid="field-phone" value={data.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+49 …" />
                </Field>
              </FieldGroup>
            )}
            {type === "sms" && (
              <FieldGroup>
                <Field label="Telefonnummer">
                  <Input data-testid="field-sms-phone" value={data.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+49 …" />
                </Field>
                <Field label="Nachricht">
                  <Input data-testid="field-sms-message" value={data.message} onChange={(e) => update("message", e.target.value)} />
                </Field>
              </FieldGroup>
            )}
            {type === "wifi" && (
              <FieldGroup>
                <Field label="Netzwerkname (SSID)">
                  <Input data-testid="field-ssid" value={data.ssid} onChange={(e) => update("ssid", e.target.value)} />
                </Field>
                <Field label="Passwort">
                  <Input data-testid="field-wifi-password" type="text" value={data.password} onChange={(e) => update("password", e.target.value)} />
                </Field>
                <Field label="Verschlüsselung">
                  <Select value={data.encryption} onValueChange={(v) => update("encryption", v)}>
                    <SelectTrigger data-testid="field-encryption"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="WPA">WPA / WPA2</SelectItem>
                      <SelectItem value="WEP">WEP</SelectItem>
                      <SelectItem value="NONE">Keine</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Verstecktes Netzwerk">
                  <div className="flex items-center h-10 gap-3">
                    <Switch data-testid="field-hidden" checked={!!data.hidden} onCheckedChange={(v) => update("hidden", v)} />
                    <span className="text-sm text-zinc-600">{data.hidden ? "Ja" : "Nein"}</span>
                  </div>
                </Field>
              </FieldGroup>
            )}
            {type === "vcard" && (
              <FieldGroup>
                <Field label="Vorname">
                  <Input data-testid="field-first-name" value={data.first_name} onChange={(e) => update("first_name", e.target.value)} />
                </Field>
                <Field label="Nachname">
                  <Input data-testid="field-last-name" value={data.last_name} onChange={(e) => update("last_name", e.target.value)} />
                </Field>
                <Field label="Firma">
                  <Input data-testid="field-org" value={data.organization} onChange={(e) => update("organization", e.target.value)} />
                </Field>
                <Field label="Position">
                  <Input data-testid="field-title" value={data.title} onChange={(e) => update("title", e.target.value)} />
                </Field>
                <Field label="Telefon">
                  <Input data-testid="field-vcard-phone" value={data.phone} onChange={(e) => update("phone", e.target.value)} />
                </Field>
                <Field label="E-Mail">
                  <Input data-testid="field-vcard-email" value={data.email} onChange={(e) => update("email", e.target.value)} />
                </Field>
                <Field label="Webseite" full>
                  <Input data-testid="field-vcard-url" value={data.url} onChange={(e) => update("url", e.target.value)} placeholder="https://…" />
                </Field>
                <Field label="Adresse" full>
                  <Input data-testid="field-vcard-address" value={data.address} onChange={(e) => update("address", e.target.value)} />
                </Field>
              </FieldGroup>
            )}
          </div>
        </div>

        <div>
          <div className="overline text-[11px] font-bold uppercase tracking-[0.22em] text-zinc-500">03 · Stil &amp; Branding</div>
          <div className="mt-3 border border-zinc-200 bg-white rounded-md p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Vordergrund</Label>
              <ColorRow value={fg} onChange={setFg} testId="fg-color" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Hintergrund</Label>
              <ColorRow value={bg} onChange={setBg} testId="bg-color" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Fehlerkorrektur</Label>
              <Select value={errorLevel} onValueChange={setErrorLevel}>
                <SelectTrigger className="mt-2" data-testid="error-level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="L">L · 7%</SelectItem>
                  <SelectItem value="M">M · 15%</SelectItem>
                  <SelectItem value="Q">Q · 25%</SelectItem>
                  <SelectItem value="H">H · 30% (empfohlen)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Rand</Label>
              <div className="mt-3 flex items-center gap-3">
                <Slider min={0} max={10} step={1} value={[border]} onValueChange={(v) => setBorder(v[0])} data-testid="border-slider" />
                <span className="text-sm font-mono w-8 text-right">{border}</span>
              </div>
            </div>

            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Mittiges Branding (optional)</Label>
              <Tabs value={centerMode} onValueChange={setCenterMode} className="mt-2" data-testid="branding-tabs">
                <TabsList className="bg-zinc-100">
                  <TabsTrigger value="none" data-testid="branding-none">Keins</TabsTrigger>
                  <TabsTrigger value="text" data-testid="branding-text">Text</TabsTrigger>
                  <TabsTrigger value="image" data-testid="branding-image">Logo</TabsTrigger>
                </TabsList>
                <TabsContent value="text" className="mt-3">
                  <Input
                    data-testid="center-text-input"
                    placeholder="z.B. MA · max 8 Zeichen"
                    maxLength={8}
                    value={centerText}
                    onChange={(e) => setCenterText(e.target.value)}
                  />
                </TabsContent>
                <TabsContent value="image" className="mt-3">
                  <div className="flex items-center gap-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      className="hidden"
                      onChange={(e) => onUpload(e.target.files?.[0])}
                      data-testid="logo-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="upload-logo-btn"
                      disabled={uploading}
                    >
                      {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-2" />}
                      Logo hochladen
                    </Button>
                    {logoFile && (
                      <>
                        <div className="flex items-center gap-2 px-2 py-1 rounded-md bg-zinc-100">
                          {logoLocalUrl && <img src={logoLocalUrl} alt="logo" className="w-6 h-6 object-contain" />}
                          <span className="text-xs text-zinc-700 max-w-[120px] truncate">{logoFile.id.slice(0, 8)}…</span>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={removeLogo} data-testid="remove-logo-btn">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-zinc-500">PNG / JPG / WEBP / SVG · max 2 MB. Wird mittig in den QR-Code eingefügt.</p>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </section>

      {/* Preview */}
      <aside className="lg:col-span-4">
        <div className="lg:sticky lg:top-24 space-y-4">
          <div className="overline text-[11px] font-bold uppercase tracking-[0.22em] text-zinc-500">Live-Vorschau</div>
          <div className="border border-zinc-200 bg-white rounded-md p-5">
            <div ref={previewRef} className="aspect-square w-full grid place-items-center rounded-md overflow-hidden border border-zinc-200" style={{ background: bg }} data-testid="qr-preview">
              {hasPayload ? (
                <div className="relative w-full h-full grid place-items-center p-4">
                  <QRCodeSVG
                    value={payload}
                    fgColor={fg}
                    bgColor={bg}
                    level={errorLevel}
                    marginSize={border}
                    includeMargin={false}
                    style={{ width: "100%", height: "100%", maxWidth: 320, maxHeight: 320 }}
                    imageSettings={
                      centerMode === "image" && logoLocalUrl
                        ? { src: logoLocalUrl, height: 56, width: 56, excavate: true }
                        : undefined
                    }
                  />
                  {centerMode === "text" && centerText && (
                    <div
                      className="absolute font-display font-extrabold tracking-tight pointer-events-none"
                      style={{
                        color: fg,
                        background: bg,
                        padding: "4px 8px",
                        borderRadius: 6,
                        fontSize: 22,
                        lineHeight: 1,
                      }}
                    >
                      {centerText}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-sm text-zinc-400 px-4">
                  <Sparkles className="w-5 h-5 mx-auto mb-2" />
                  Inhalt eingeben um Vorschau zu sehen
                </div>
              )}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <Button
                onClick={() => generateAndDownload("png")}
                className="bg-[#0A52FF] hover:bg-[#003ECC] text-white h-10"
                disabled={!hasPayload || generating}
                data-testid="download-png-btn"
              >
                {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                PNG
              </Button>
              <Button
                onClick={() => generateAndDownload("svg")}
                variant="outline"
                className="h-10"
                disabled={!hasPayload || generating}
                data-testid="download-svg-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                SVG
              </Button>
            </div>
            <div className="mt-3 text-[11px] text-zinc-500 font-mono break-all max-h-16 overflow-auto" data-testid="payload-preview">
              {payload || "—"}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

function FieldGroup({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">{children}</div>;
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-xs uppercase tracking-wider text-zinc-500">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function ColorRow({ value, onChange, testId }) {
  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-12 h-10 rounded-md border border-zinc-200 cursor-pointer bg-white"
          data-testid={`${testId}-input`}
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="font-mono uppercase"
          data-testid={`${testId}-hex`}
        />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {COLOR_PRESETS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            className="color-swatch"
            style={{ background: c, outline: value.toLowerCase() === c.toLowerCase() ? "2px solid #0A52FF" : "none", outlineOffset: 2 }}
            data-testid={`${testId}-preset-${c.replace("#", "")}`}
            aria-label={`Farbe ${c}`}
          />
        ))}
      </div>
    </div>
  );
}

function downloadDataUrl(href, filename) {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
